import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Sparkle, ArrowUp, ArrowDown } from "@phosphor-icons/react";

/**
 * SignupTrendTile — a denser replacement for the simple "New (24h)"
 * KPI tile. Renders inline:
 *   - Today's signup count (big number, primary if > 0)
 *   - Inline 7-day SVG sparkline (28px tall) so the admin sees the
 *     shape of the week, not just a single number
 *   - Tiny vs-prior-avg delta badge (green/red arrow) — only renders
 *     when the prior 6-day window had at least 1 signup, so we never
 *     show "+∞%" or a misleading +100% off a zero baseline
 *
 * Props:
 *   kind: "all" | "owners" | "customers"  — which series to render
 *     ("all" = `count`, "owners" = `owners`, "customers" = `customers`)
 *   label: tile label override (default depends on kind)
 *   fallbackToday: parent's local-computed today count, used if the
 *     /admin/signup-trend endpoint errors
 *
 * Failure modes are quiet: if the trend endpoint errors, we fall back
 * to the simple `value` prop the parent already computed (last-24h).
 */
export default function SignupTrendTile({ kind = "all", label, fallbackToday, testId }) {
  const [trend, setTrend] = useState(null);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    let mounted = true;
    api
      .get("/admin/signup-trend")
      .then(({ data }) => mounted && setTrend(data))
      .catch(() => mounted && setErrored(true));
    return () => {
      mounted = false;
    };
  }, []);

  const seriesKey = kind === "owners" ? "owners" : kind === "customers" ? "customers" : "count";
  const days = (trend?.days || []).map((d) => ({ ...d, value: d[seriesKey] }));
  const today = days.length ? days[days.length - 1].value : (fallbackToday ?? 0);
  // Series-specific delta (recompute against prior 6 days for the chosen
  // kind, so the % is meaningful for owners/customers slices, not just
  // the combined total).
  const prior = days.slice(0, -1).map((d) => d.value);
  const priorAvgRaw = prior.length ? prior.reduce((a, b) => a + b, 0) / prior.length : 0;
  const delta = priorAvgRaw > 0 ? Math.round(((today - priorAvgRaw) / priorAvgRaw) * 100) : null;
  const total = days.reduce((a, b) => a + b.value, 0);
  const avgPerDay = days.length ? Math.round((total / days.length) * 10) / 10 : 0;

  const tileLabel = label || (kind === "owners" ? "New trucks (24h)" : kind === "customers" ? "New customers (24h)" : "New (24h)");
  const tileTestId = testId || (kind === "owners" ? "admin-kpi-new-trucks-24h" : kind === "customers" ? "admin-kpi-new-customers-24h" : "admin-kpi-new-24h");

  // Sparkline geometry — 7 points across 100 units wide, 28 tall.
  // Polyline plus stacked area below it for that "trending" weight.
  const max = Math.max(1, ...days.map((d) => d.value));
  const w = 100;
  const h = 28;
  const stepX = days.length > 1 ? w / (days.length - 1) : w;
  const points = days.map((d, i) => {
    const x = i * stepX;
    const y = h - (d.value / max) * (h - 4) - 2;
    return [x, y];
  });
  const linePath = points.map(([x, y], i) => (i === 0 ? `M${x},${y}` : `L${x},${y}`)).join(" ");
  const areaPath = points.length
    ? `${linePath} L${points[points.length - 1][0]},${h} L0,${h} Z`
    : "";

  const tone = today > 0 ? "primary" : "muted";
  const deltaTone =
    delta == null ? "muted" : delta > 0 ? "up" : delta < 0 ? "down" : "flat";
  const gradientId = `sparkFill-${kind}`;

  return (
    <div
      className={`rounded-2xl border bg-card p-4 ${
        tone === "primary" ? "border-primary/40 ring-1 ring-primary/10" : "border-border"
      }`}
      data-testid={tileTestId}
    >
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">{tileLabel}</div>
        <Sparkle weight="duotone" className="h-4 w-4 text-primary" />
      </div>
      <div className="mt-2 flex items-end gap-2">
        <div className="font-display font-bold text-2xl leading-none">{today}</div>
        {deltaTone !== "muted" && (
          <span
            className={
              "inline-flex items-center gap-0.5 text-[11px] font-semibold rounded-full px-1.5 py-0.5 " +
              (deltaTone === "up"
                ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                : deltaTone === "down"
                ? "bg-rose-500/10 text-rose-600 dark:text-rose-400"
                : "bg-muted text-muted-foreground")
            }
            data-testid={`${tileTestId}-delta`}
            title={`vs. prior 6-day avg (${avgPerDay}/day)`}
          >
            {deltaTone === "up" ? (
              <ArrowUp weight="bold" className="h-2.5 w-2.5" />
            ) : (
              <ArrowDown weight="bold" className="h-2.5 w-2.5" />
            )}
            {Math.abs(delta)}%
          </span>
        )}
      </div>
      {!errored && days.length > 0 && (
        <svg
          viewBox={`0 0 ${w} ${h}`}
          preserveAspectRatio="none"
          className="mt-2 w-full h-7 overflow-visible"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgb(234 88 12)" stopOpacity="0.35" />
              <stop offset="100%" stopColor="rgb(234 88 12)" stopOpacity="0" />
            </linearGradient>
          </defs>
          {areaPath && <path d={areaPath} fill={`url(#${gradientId})`} />}
          {linePath && (
            <path
              d={linePath}
              fill="none"
              stroke="rgb(234 88 12)"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          )}
          {points.length > 0 && (
            <circle
              cx={points[points.length - 1][0]}
              cy={points[points.length - 1][1]}
              r="2"
              fill="rgb(234 88 12)"
            />
          )}
        </svg>
      )}
      <div className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">
        7-day · avg {avgPerDay}/day
      </div>
    </div>
  );
}
